import type { License, UnsignedLicense } from './types.js';

/**
 * Canonical JSON serialization: keys sorted lexicographically at every depth,
 * no whitespace. Both signer and verifier produce identical bytes for the
 * same logical object — that's the whole point.
 *
 * Rules that differ from `JSON.stringify`:
 *   - object keys with `undefined` values are dropped (matches JSON.stringify)
 *   - keys are sorted at every depth (the canonicalizing bit)
 *   - non-finite numbers (NaN, ±Infinity) throw rather than serialize as null
 */
export function canonicalize(value: unknown): string {
  return serialize(value);
}

function serialize(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) {
      throw new Error('canonicalize: non-finite numbers are not representable in JSON');
    }
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) {
    return '[' + v.map(serialize).join(',') + ']';
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>).filter(
      ([, val]) => val !== undefined,
    );
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return (
      '{' + entries.map(([k, val]) => JSON.stringify(k) + ':' + serialize(val)).join(',') + '}'
    );
  }
  throw new Error(`canonicalize: unsupported value type ${typeof v}`);
}

/**
 * Strip the `signature` field (if present) and return UTF-8 canonical-JSON
 * bytes — the message that gets signed/verified. Shared by any signed envelope
 * (license, revocation list).
 */
export function signedMessageBytes(value: object): Uint8Array {
  const obj: Record<string, unknown> = { ...value };
  delete obj['signature'];
  return new TextEncoder().encode(canonicalize(obj));
}

/** Message bytes for a license — an alias of {@link signedMessageBytes}. */
export function licenseMessageBytes(license: License | UnsignedLicense): Uint8Array {
  return signedMessageBytes(license);
}
